/* Turning the core's generic words into the pack's own.
 *
 * The core says "With checker" and "Submit for check"; a pack may call its
 * approver "PM" or "Partner". Every label a person reads goes through here, so
 * the screens never name an industry and never show the core's jargon where
 * the pack has a word of its own.
 */
import type { ActivePack, Kind, PacksActive, WorkRow, WorkStep, Who } from './types'

export const STATES = {
  OPEN: 'Open', WITH_CHECKER: 'With checker', WITH_REVIEWER: 'With reviewer', WITH_APPROVER: 'With approver',
  INFO_REQUESTED: 'Info requested', REVISE: 'Revise', APPROVED: 'Approved',
  REJECTED: 'Rejected', PENDING: 'Pending', COMPLETED: 'Completed',
} as const

export const ACTIONS = {
  SUBMIT: 'Submit for check', REQUEST_INFO: 'Request info', RESUME: 'Resume',
  APPROVE: 'Approve', SEND_BACK: 'Revise', REJECT: 'Reject', COMPLETE: 'Complete',
  MARK_PENDING: 'Mark pending',
} as const

type Words = Kind['words']

const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1)

/** A role's word inside a sentence ("your turn as checker"): lower case, except a short form
 *  that is written in capitals ("PM", "GM"), which reads wrong as "pm". */
export function roleWord(word: string): string {
  return /^[A-Z0-9&]{2,}$/.test(word) ? word : lower(word)
}

/** The pack's word for a role ("PM", "Partner"), or the core's plain one. */
export function whoWord(who: Who | null | undefined, words: Words = {}): string {
  if (!who) return ''
  if (who === 'assignee') return words.maker ?? 'Assignee'
  return words[who] ?? (who === 'maker' ? 'Preparer' : who === 'checker' ? 'Checker'
    : who === 'reviewer' ? 'Reviewer' : 'Approver')
}

/** The roles that may take a step: a pack names one, several, or nobody (everyone). */
export function rolesOf(role: string | string[] | undefined): string[] {
  if (!role) return []
  return (Array.isArray(role) ? role : [role]).filter((r) => r && r !== 'everyone')
}

/** Whether a kind has the optional review stage after its checker. */
export function hasReviewer(kind: Pick<Kind, 'roles'> | null | undefined): boolean {
  return Boolean(kind?.roles.reviewer)
}

/** The home's title: the pack's own ("My jobs"), else a plain one. */
export function homeLabel(active: PacksActive | null | undefined): string {
  const packs = active?.packs ?? []
  if (packs.length === 1 && packs[0]?.home_label) return packs[0].home_label
  return 'My work'
}

export function stateLabel(state: string | null | undefined, words: Words = {}, flow?: string | null): string {
  const s = state || STATES.OPEN
  if (s === STATES.WITH_CHECKER) return `With ${words.checker ?? 'checker'}`
  if (s === STATES.WITH_REVIEWER) return `With ${words.reviewer ?? 'reviewer'}`
  if (s === STATES.WITH_APPROVER) return `With ${words.approver ?? 'approver'}`
  if (s === STATES.OPEN && flow === 'review') return `With ${lower(words.maker ?? 'preparer')}`
  if (s === STATES.OPEN && flow === 'decide') return 'Waiting for a decision'
  if (s === STATES.REVISE) return 'Sent back'
  return s
}

/** A chip colour for a state, from the shared palette. */
export function stateTone(state: string | null | undefined): 'ok' | 'warn' | 'danger' | 'info' | 'draft' {
  switch (state) {
    case STATES.APPROVED: case STATES.COMPLETED: return 'ok'
    case 'Working': return 'info'
    case STATES.REJECTED: return 'danger'
    case STATES.REVISE: case STATES.INFO_REQUESTED: case STATES.PENDING: return 'warn'
    case STATES.WITH_CHECKER: case STATES.WITH_REVIEWER: case STATES.WITH_APPROVER: return 'info'
    default: return 'draft'
  }
}

/** What a step's button says. The action sent stays the core's own. */
export function stepLabel(step: WorkStep, words: Words = {}): string {
  switch (step.action) {
    case ACTIONS.SUBMIT: return `Send to ${words.checker ?? 'checker'}`
    case ACTIONS.REQUEST_INFO: return 'Ask for information'
    case ACTIONS.RESUME: return 'Carry on'
    case ACTIONS.APPROVE:
      return step.next_state === STATES.WITH_APPROVER
        ? `Approve and send to ${words.approver ?? 'approver'}`
        : step.next_state === STATES.WITH_REVIEWER
          ? `Approve and send to ${words.reviewer ?? 'reviewer'}` : 'Approve'
    case ACTIONS.SEND_BACK: return `Send back to ${lower(words.maker ?? 'preparer')}`
    case ACTIONS.COMPLETE: return 'Mark completed'
    case ACTIONS.MARK_PENDING: return 'Mark pending'
    case ACTIONS.REJECT: return 'Reject'
    default: return step.action
  }
}

/** The steps that need the person to write something first. */
export function stepNeeds(step: WorkStep): 'reply' | 'reason' | 'note' | 'nothing' {
  if (step.action === ACTIONS.SUBMIT || step.action === ACTIONS.COMPLETE) return 'reply'
  if (step.action === ACTIONS.REQUEST_INFO) return 'reason'
  if (step.action === ACTIONS.SEND_BACK || step.action === ACTIONS.REJECT) return 'note'
  return 'nothing'
}

export type Bucket = 'overdue' | 'today' | 'this-week' | 'next-week' | 'later' | 'none'

export const BUCKETS: { key: Bucket; label: string }[] = [
  { key: 'overdue', label: 'Overdue' },
  { key: 'today', label: 'Due today' },
  { key: 'this-week', label: 'This week' },
  { key: 'next-week', label: 'Next week' },
  { key: 'later', label: 'Later' },
  { key: 'none', label: 'No deadline' },
]

const dayNumber = (iso: string) => {
  const [y = 1970, m = 1, d = 1] = iso.slice(0, 10).split('-').map(Number)
  return Date.UTC(y, m - 1, d) / 864e5
}

/** Which deadline column a row sits in. `today` comes from the server, in
 *  the organisation's time zone, never from the browser's clock: a browser on
 *  UTC put a whole day's work in "tomorrow" once before. */
export function bucketOf(row: Pick<WorkRow, 'exp_end_date' | 'closed' | 'overdue'>, today: string): Bucket {
  const end = row.exp_end_date ? String(row.exp_end_date).slice(0, 10) : ''
  if (!end) return 'none'
  if (row.overdue) return 'overdue'
  const t = dayNumber(today)
  const e = dayNumber(end)
  if (e < t) return row.closed ? 'later' : 'overdue'
  if (e === t) return 'today'
  // Weeks run Monday to Sunday.
  const weekday = (new Date(t * 864e5).getUTCDay() + 6) % 7
  const sunday = t + (6 - weekday)
  if (e <= sunday) return 'this-week'
  if (e <= sunday + 7) return 'next-week'
  return 'later'
}

export function findKind(active: PacksActive | null, value: string | null | undefined):
    { pack: ActivePack; kind: Kind } | null {
  if (!active || !value) return null
  const [code, key] = value.split('.', 2)
  const pack = active.packs.find((p) => p.code === code)
  const kind = pack?.kinds.find((k) => k.key === key)
  return pack && kind ? { pack, kind } : null
}

/** The pack's own priority word on a row, when it has one (a field that
 *  mirrors ERPNext's priority). */
export function priorityOf(row: WorkRow, kind: Kind | undefined): string | null {
  const field = kind?.fields.find((f) => f.mirror)
  const value = field ? row[field.fieldname] : null
  return value ? String(value) : null
}

const dayIso = (n: number) => new Date(n * 864e5).toISOString().slice(0, 10)

/** The deadline a card takes when dropped on a column: the end of that period, as people mean
 *  it (Monday-to-Sunday weeks); null clears it. Overdue takes nothing: nobody plans to be late.
 *  `today` is the server's, in the organisation's time zone. */
export function bucketDate(bucket: Bucket, today: string): string | null | undefined {
  const t = dayNumber(today)
  const weekday = (new Date(t * 864e5).getUTCDay() + 6) % 7
  const sunday = t + (6 - weekday)
  switch (bucket) {
    case 'today': return dayIso(t)
    case 'this-week': return dayIso(sunday)
    case 'next-week': return dayIso(sunday + 7)
    case 'later': return dayIso(sunday + 14)
    case 'none': return null
    default: return undefined
  }
}

/** Does a row match what was typed in the search box: its title, code, kind, state, project,
 *  client or the people on it (by name). Every word must match somewhere. */
export function matchesSearch(row: WorkRow, query: string, extra: string[] = []): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return true
  const hay = [row.subject, row.name, row.kind_label, row.state_label, row.project, row.project_title,
               row.customer, ...extra].filter(Boolean).join(' ').toLowerCase()
  return words.every((w) => hay.includes(w))
}

/** What the Jobs table sorts by (owner, 2026-10-09: "sorting is missing in table view"). The same
 *  words the gateway takes for a page, so both modes sort alike. */
export type TableSort = 'deadline' | 'start' | 'task' | 'state' | 'project' | 'updated' | 'assignee' | 'raised_by'

/** Rows in the table's order: dates with the undated last either way (as the gateway pages them),
 *  words by how they read, the row's number breaking ties. Pure. */
export function sortRows(rows: WorkRow[], sort: TableSort, dir: 'asc' | 'desc',
                         label: (r: WorkRow) => string = (r) => r.workflow_state ?? ''): WorkRow[] {
  const key = (r: WorkRow): string => {
    switch (sort) {
      case 'deadline': return String(r.exp_end_date ?? '').slice(0, 10)
      case 'start': return String(r.exp_start_date ?? '').slice(0, 10)
      case 'task': return (r.subject ?? '').toLowerCase()
      case 'state': return label(r).toLowerCase()
      case 'project': return (r.project_title ?? r.project ?? '').toLowerCase()
      case 'updated': return String(r.modified ?? '')
      // By address, as the gateway sorts a long list: the same order either way.
      case 'assignee': return [...(r.assigned ?? [])].sort().join(',').toLowerCase()
      case 'raised_by': return String(r.owner ?? '').toLowerCase()
    }
  }
  const sign = dir === 'desc' ? -1 : 1
  const dates = sort === 'deadline' || sort === 'start'
  return [...rows].sort((a, b) => {
    const ka = key(a); const kb = key(b)
    if (dates && !ka !== !kb) return ka ? -1 : 1
    const c = ka < kb ? -1 : ka > kb ? 1 : 0
    return c ? c * sign : (a.name < b.name ? -1 : a.name > b.name ? 1 : 0) * sign
  })
}
